import { Injectable } from '@angular/core';
import { HttpContext, HttpContextToken } from '@angular/common/http';
import { APP_RELEASE } from '../app-release.generated';
import { AccountApiService } from '../api/services';
import { AppVersionService } from './app-version.service';

const MAX_RECENT_DIAGNOSTICS = 80;
export const SPARK_DIAGNOSTIC_REQUEST = new HttpContextToken<boolean>(() => false);

/** Diagnostic hints only; never used for authentication or authorization. */
export function getSparkClientContext(
  nav: Pick<Navigator, 'userAgent' | 'platform' | 'maxTouchPoints'> = navigator,
  win: any = window
): { source: string; browser: string; platform: string; userAgent: string } {
  const ua = nav.userAgent || '';
  // iPadOS can request desktop sites and identify itself as a Mac.
  const ios = /iPad|iPhone|iPod/i.test(ua) ||
    (/Mac/i.test(nav.platform || '') && nav.maxTouchPoints > 1);
  const native = !!win.webkit?.messageHandlers?.sparkDiagnostics || /OptomiserApp\/iOS/i.test(ua);
  const browser = /EdgiOS|EdgA|Edg\//i.test(ua) ? 'Edge' :
    /OPiOS|OPR\//i.test(ua) ? 'Opera' :
    /CriOS|Chrome|Chromium/i.test(ua) ? 'Chrome' :
    /FxiOS|Firefox/i.test(ua) ? 'Firefox' :
    /Version\/.*Safari/i.test(ua) ? 'Safari' : 'Unknown';
  const mobile = ios || /Android|Mobile/i.test(ua);
  return {
    source: native ? 'opto-ios-app' : ios && browser === 'Safari' ? 'ios-safari' :
      mobile ? 'other-mobile-browser' : 'desktop-browser',
    browser: native ? 'WKWebView' : browser,
    platform: ios || native ? 'iOS' : /Android/i.test(ua) ? 'Android' :
      /Win/i.test(nav.platform || '') ? 'Windows' : /Mac/i.test(nav.platform || '') ? 'macOS' : 'Other',
    userAgent: ua,
  };
}

@Injectable({ providedIn: 'root' })
export class FrontendDiagnosticsService {
  private entries: string[] = [];
  private sentEvents = 0;
  readonly entryId: string;
  readonly entrySource: string;

  constructor(private accountApiService: AccountApiService, private appVersionService: AppVersionService) {
    const query = new URLSearchParams(window.location.search);
    const suppliedId = query.get('sparkEntryId');
    const validId = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    let stored: { id?: string; source?: string } = {};
    try {
      const value = JSON.parse(sessionStorage.getItem('spark-entry') || '{}');
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        stored = value;
      }
    } catch { /* Diagnostics must not prevent login when storage is unavailable. */ }
    this.entryId = suppliedId && validId.test(suppliedId) ? suppliedId :
      stored.id && validId.test(stored.id) ? stored.id :
      'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const n = Math.floor(Math.random() * 16);
        return (c === 'x' ? n : (n & 3) | 8).toString(16);
      });
    const source = suppliedId ? query.get('sparkEntrySource') : stored.source;
    const sources = ['menu', 'client-menu', 'service-create', 'service-open', 'service-view',
      'service-programs', 'opto-ios-tab', 'opto-link', 'direct'];
    this.entrySource = source && sources.includes(source) ? source :
      getSparkClientContext().source === 'opto-ios-app' ? 'opto-ios-tab' : 'direct';
    try {
      sessionStorage.setItem('spark-entry', JSON.stringify({ id: this.entryId, source: this.entrySource }));
    } catch { /* Keep the in-memory correlation when storage is unavailable. */ }
  }

  /** Automatic, bounded telemetry; never includes credentials, patient data or full URLs. */
  recordEntry(event: string, details: Record<string, unknown> = {}): void {
    if (this.sentEvents >= 40) {
      return;
    }
    this.sentEvents++;
    const client = getSparkClientContext();
    const connection = (navigator as any).connection;
    let timeZone = '';
    try { timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { }
    const context: Record<string, unknown> = {
      Event: event,
      EntryId: this.entryId,
      EntrySource: this.entrySource,
      Client: client.source,
      Browser: client.browser,
      Platform: client.platform,
      UserAgent: client.userAgent.slice(0, 512),
      DeviceUtc: new Date().toISOString(),
      TimeZone: timeZone,
      Online: navigator.onLine,
      ConnectionType: connection?.type,
      EffectiveType: connection?.effectiveType,
      Rtt: connection?.rtt,
      Downlink: connection?.downlink,
      SaveData: connection?.saveData,
      ScreenWidth: window.screen.width,
      ScreenHeight: window.screen.height,
      AppVersion: this.appVersionService.getVersionRaw(),
    };
    for (const name of ['HasAccessToken', 'HasUserInfo', 'AccessExpiresAt', 'RefreshExpiresAt',
      'AccessRemainingMs', 'Route', 'Status']) {
      const value = details[name];
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        context[name] = name === 'Route' && typeof value === 'string'
          ? value.split('?')[0].replace(/\d+/g, ':id').slice(0, 200) : value;
      }
    }
    this.accountApiService.LogNavigation({ body: {
      Method: 'SPARK_ENTRY',
      Context: JSON.stringify(context),
    } }, new HttpContext().set(SPARK_DIAGNOSTIC_REQUEST, true)).subscribe({
      error: () => { /* Reporting must never refresh, sign out or interrupt the user. */ },
    });
  }

  addError(message: string, stack?: string): void {
    const stamp = new Date().toISOString() + ' Spark ' + APP_RELEASE.version + ' (' + APP_RELEASE.buildId + ')';
    const entry = stack
      ? `[${stamp}] ERROR: ${message}\n${stack}`
      : `[${stamp}] ERROR: ${message}`;
    this.push(entry);
  }

  addDiagnostic(message: string, context?: string): void {
    const stamp = new Date().toISOString() + ' Spark ' + APP_RELEASE.version + ' (' + APP_RELEASE.buildId + ')';
    const entry = context
      ? `[${stamp}] TRACE: ${message} | ${context}`
      : `[${stamp}] TRACE: ${message}`;
    this.push(entry);
    if (/^(signin-view-init|token-handoff-|manual-signin-|auth-guard-blocked-navigation)/.test(message)) {
      // Breadcrumb contexts can contain routes; transmit only explicitly safe fields.
      let data: any = {};
      try { data = JSON.parse(context || '{}'); } catch { }
      this.recordEntry(message, {
        HasAccessToken: data.hasTokenInfo ?? data.hasAccessToken,
        HasUserInfo: data.hasUserInfo,
        AccessExpiresAt: data.tokenExpiresAt ?? data.expiresAt,
        RefreshExpiresAt: data.refreshExpiresAt,
        Route: typeof data.url === 'string' ? data.url.split('?')[0].replace(/\d+/g, ':id') : undefined,
      });
    }
  }

  getRecentEntries(): string[] {
    return [...this.entries];
  }

  private push(entry: string): void {
    const client = getSparkClientContext();
    this.entries.push('[' + client.source + '; ' + client.browser + '; ' + client.platform + '] ' + entry);
    if (this.entries.length > MAX_RECENT_DIAGNOSTICS) {
      this.entries.shift();
    }
  }
}
